import React, { useState, useMemo } from 'react';
import {
  Flex,
  Box,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Tag,
  TagLabel,
  TagCloseButton,
  VStack,
} from '@chakra-ui/react';
import { MagnifyingGlass, Calendar } from 'phosphor-react';
import { IDossieFiltersProps } from '../utils/dossieTypes';

// Helper para obter o último dia do mês
const getLastDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

// Helper para formatar mês/ano para exibição
const formatMonthYear = (dateStr: string): string => {
  const [year, month] = dateStr.split('-');
  const monthNames = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  return `${monthNames[parseInt(month, 10) - 1]}/${year}`;
};

export function DossieFilters({ filters, onFilterChange }: IDossieFiltersProps) {
  const getMonthFromDate = (dateStr: string) => dateStr.substring(0, 7);

  const [startMonth, setStartMonth] = useState(getMonthFromDate(filters.date_start));
  const [endMonth, setEndMonth] = useState(getMonthFromDate(filters.date_end));
  const [fullYearFilter, setFullYearFilter] = useState<number | null>(null);

  const currentYear = new Date().getFullYear();

  const monthToStartDate = (month: string): string => `${month}-01`;

  const monthToEndDate = (month: string): string => {
    const [year, monthNum] = month.split('-').map(Number);
    const lastDay = getLastDayOfMonth(year, monthNum - 1);
    return `${month}-${String(lastDay).padStart(2, '0')}`;
  };

  const handleApplyFilters = () => {
    if (fullYearFilter) {
      onFilterChange({
        date_start: `${fullYearFilter}-01-01`,
        date_end: `${fullYearFilter}-12-31`,
      });
    } else {
      onFilterChange({
        date_start: monthToStartDate(startMonth),
        date_end: monthToEndDate(endMonth),
      });
    }
  };

  const handleFullYearFilter = () => {
    setFullYearFilter(currentYear);
    onFilterChange({
      date_start: `${currentYear}-01-01`,
      date_end: `${currentYear}-12-31`,
    });
  };

  const handleRemoveFullYearFilter = () => {
    setFullYearFilter(null);
  };

  const periodText = useMemo(() => {
    if (fullYearFilter) {
      return `Ano ${fullYearFilter}`;
    }
    if (startMonth === endMonth) {
      return formatMonthYear(startMonth);
    }
    return `${formatMonthYear(startMonth)} a ${formatMonthYear(endMonth)}`;
  }, [fullYearFilter, startMonth, endMonth]);

  return (
    <Box bg="white" borderRadius="xl" border="1px" borderColor="gray.200" p={3} mb={4}>
      <VStack spacing={3} align="stretch">
        {/* Linha 1: Filtro de ano + Período */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          {fullYearFilter ? (
            <Tag size="md" colorScheme="blue" borderRadius="full" variant="solid">
              <Calendar size={14} style={{ marginRight: 6 }} />
              <TagLabel fontSize="sm">{fullYearFilter}</TagLabel>
              <TagCloseButton onClick={handleRemoveFullYearFilter} />
            </Tag>
          ) : (
            <Button
              leftIcon={<Calendar size={14} />}
              size="xs"
              variant="outline"
              colorScheme="blue"
              onClick={handleFullYearFilter}
            >
              {currentYear}
            </Button>
          )}
          <Text fontSize="xs" color="gray.600">
            <Text as="span" fontWeight="bold" color="blue.600">
              {periodText}
            </Text>
          </Text>
        </Flex>

        {/* Linha 2: Filtros de mês */}
        {!fullYearFilter && (
          <VStack spacing={2} align="stretch">
            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>
                Início
              </FormLabel>
              <Input
                type="month"
                size="sm"
                value={startMonth}
                onChange={e => setStartMonth(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>
                Fim
              </FormLabel>
              <Input
                type="month"
                size="sm"
                value={endMonth}
                onChange={e => setEndMonth(e.target.value)}
              />
            </FormControl>
            <Button
              leftIcon={<MagnifyingGlass size={14} />}
              size="sm"
              colorScheme="blue"
              onClick={handleApplyFilters}
              w="100%"
            >
              Filtrar
            </Button>
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
